import { useState, useEffect } from 'react';
import API from '../../api/axiosConfig';
import TrainingForm from './TrainingForm';
import { getCurrentPeriod } from "../../Utils/getCurrentPeriod";

const TYPE_BADGE = {
  1: { label: 'داخلي',    bg: '#dbeafe', color: '#1d4ed8' },
  2: { label: 'خارجي',    bg: '#ede9fe', color: '#7c3aed' },
  3: { label: 'إلكتروني', bg: '#ccfbf1', color: '#0f766e' },
};

const cardStyle = {
  backgroundColor: 'var(--background-color)',
  border: '1px solid var(--border-color)',
  borderRadius: '12px',
  padding: '16px',
  textAlign: 'center',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

const TrainingList = ({ period = getCurrentPeriod() }) => {
  const [data,    setData]    = useState({ data: [], stats: {}, meta: {} });
  const [loading, setLoading] = useState(true);
  const [showForm,setShowForm]= useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await API.get('/training-records', { params: { period } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [period]);

  const handleDelete = async (id) => {
    if (!window.confirm('هل تريد حذف هذا السجل؟')) return;
    await API.delete(`/training-records/${id}`);
    load();
  };

  const stats = data.stats ?? {};

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }} dir="rtl">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 style={{ color: 'var(--text-color)', fontWeight: 600, marginBottom: 4 }}>
            سجلات التدريب
          </h2>
          <p style={{ color: 'var(--text-muted, #6b7280)', fontSize: 13, margin: 0 }}>
            الفترة: {period}
          </p>
        </div>
        <button
          onClick={() => { setShowForm(s => !s); setEditing(null); }}
          style={{
            backgroundColor: '#0f766e',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            padding: '8px 18px',
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          {showForm ? 'إغلاق' : '+ إضافة تدريب'}
        </button>
      </div>

      {/* Form */}
      {(showForm || editing) && (
        <div className="mb-4">
          <TrainingForm
            period={period}
            initialData={editing}
            onSuccess={() => { setShowForm(false); setEditing(null); load(); }}
          />
        </div>
      )}

      {/* Stats */}
      <div className="row g-3 mb-4">
        {[
          { label: 'إجمالي الساعات',  value: stats.total_hours     ?? 0,   color: '#0f766e' },
          { label: 'عدد الدورات',     value: stats.total_sessions  ?? 0,   color: '#1f2937' },
          { label: 'ساعة / موظف',     value: stats.avg_per_employee ?? '—', color: '#7c3aed' },
        ].map(s => (
          <div className="col-4" key={s.label}>
            <div style={cardStyle}>
              <p style={{ fontSize: 24, fontWeight: 700, color: s.color, margin: 0 }}>{s.value}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)', marginTop: 4 }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted, #9ca3af)' }}>
          جاري التحميل...
        </div>
      ) : data.data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted, #9ca3af)', backgroundColor: 'var(--background-color)', borderRadius: 12 }}>
          <div style={{ fontSize: 40 }}>📚</div>
          <p>لا توجد سجلات تدريب لهذه الفترة</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'var(--background-color)', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
          <table className="table mb-0" style={{ color: 'var(--text-color)' }}>
            <thead style={{ backgroundColor: 'var(--border-color)', color: 'var(--text-color)' }}>
              <tr></tr>
              <tr>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>الموظف</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>عنوان الدورة</th>
                <th style={{ textAlign: 'center', padding: '12px 16px' }}>الساعات</th>
                <th style={{ textAlign: 'center', padding: '12px 16px' }}>النوع</th>
                <th style={{ textAlign: 'center', padding: '12px 16px' }}>التاريخ</th>
                <th style={{ textAlign: 'center', padding: '12px 16px' }}>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map(row => {
                const badge = TYPE_BADGE[row.type];
                return (
                  <tr key={row.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 500 }}>{row.employee_name ?? '—'}</td>
                    <td style={{ padding: '12px 16px' }}>{row.title}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#0f766e' }}>
                      {row.hours}h
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        backgroundColor: badge?.bg ?? '#f3f4f6',
                        color: badge?.color ?? '#374151',
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 500,
                      }}>
                        {badge?.label ?? row.type_label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>
                      {row.training_date}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button onClick={() => { setEditing(row); setShowForm(false); }}
                        style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 12, cursor: 'pointer', marginLeft: 8 }}>
                        تعديل
                      </button>
                      <button onClick={() => handleDelete(row.id)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>
                        حذف
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TrainingList;